import type { UseQueryOptionsType } from './UseQueryOptionsType';
import type { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type {
  SubscriptionRequestCreateDTO,
  SubscriptionRequestFilterDTO,
  SubscriptionRequestHistoryCreateDTO,
  SubscriptionRequestHistoryDeleteRq,
  SubscriptionRequestHistoryUpdateDTO,
  SubscriptionRequestUpdateDTO,
} from '@repositories/subscriptionRequestRepository/subscriptionRequest.types';
import type { ISubscriptionRequestRepository } from '@repositories/subscriptionRequestRepository/ISubscriptionRequestRepository';
import type { Page } from '@type/Page';
import { SubscriptionRequestModel } from '@repositories/subscriptionRequestRepository/models/SubscriptionRequestModel';

const sbReqRepo = repository.get<ISubscriptionRequestRepository>(KEYS.SUBSCRIPTION_REQUEST_REPOSITORY);
const sbReqQueryKey = {
  all: ['subscriptionRequest'] as const,
  lists: () => [...sbReqQueryKey.all, 'list'] as const,
  details: () => [...sbReqQueryKey.all, 'detail'] as const,

  list: (filter: SubscriptionRequestFilterDTO) => [...sbReqQueryKey.lists(), filter] as const,
  detail: (id: number) => [...sbReqQueryKey.details(), id] as const,
};

export const useGetSubscriptionRequests = (
  queries: SubscriptionRequestFilterDTO,
  options?: UseQueryOptionsType<Page<SubscriptionRequestModel>>,
) =>
  useQuery<Page<SubscriptionRequestModel>, AxiosError>(
    sbReqQueryKey.list({ ...queries }),
    async () => {
      const result = await sbReqRepo.getList(queries);
      return {
        ...result,
        content: result.content.map(dto => new SubscriptionRequestModel(dto)),
      };
    },
    {
      ...options,
    },
  );

export const useGetSubscriptionRequest = (id: number, options?: UseQueryOptionsType<SubscriptionRequestModel>) => {
  const result = useQuery<SubscriptionRequestModel, AxiosError>(
    sbReqQueryKey.detail(id),
    async () => {
      const result = await sbReqRepo.getOne(id);
      return new SubscriptionRequestModel(result);
    },
    {
      ...options,
      enabled: !!id && id > 0 && (options?.enabled !== undefined ? options.enabled : true),
    },
  );

  const initialData = useMemo(() => new SubscriptionRequestModel(), []);
  return { ...result, data: result.data ?? initialData };
};

export const useAddSubscriptionRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: SubscriptionRequestCreateDTO) => {
      await sbReqRepo.create(data);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
      },
    },
  );
};

export const useUpdateSubscriptionRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: SubscriptionRequestUpdateDTO) => {
      const { id } = await sbReqRepo.update(data.id, data);
      return id;
    },
    {
      onSuccess: (id: number) => {
        queryClient.invalidateQueries({ queryKey: sbReqQueryKey.detail(id) });
        queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
      },
    },
  );
};

export const useDeleteSubscriptionRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      await sbReqRepo.delete(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
      },
    },
  );
};

export const useCreateSubscriptionRequestHistoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SubscriptionRequestHistoryCreateDTO) => {
      const result = await sbReqRepo.createHistory(request);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.details() });
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
    },
  });
};

export const useUpdateSubscriptionRequestHistoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SubscriptionRequestHistoryUpdateDTO) => {
      const result = await sbReqRepo.updateHistory(request);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.details() });
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
    },
  });
};

export const useDeleteSubscriptionRequestHistoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rqData: SubscriptionRequestHistoryDeleteRq) => {
      await sbReqRepo.deleteHistory(rqData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.details() });
      queryClient.invalidateQueries({ queryKey: sbReqQueryKey.lists() });
    },
  });
};
